import React, { useRef, useEffect, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { useTheme } from "../../../context/ThemeContext";

import "../../modules/AuthEvents/ThreatTimeLineChart/ThreatTimeLineChart.css";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler);

const RiskTimeLineChart = ({ data, loading }) => {
  const chartRef = useRef(null);
  const [gradientOrange, setGradientOrange] = useState("rgba(249,115,22,0.1)");
  const [gradientBlue, setGradientBlue] = useState("rgba(59,130,246,0.1)");

  const { isDark } = useTheme();

  const chartData = {
    labels: data?.slice(0,10).map((item) => item?.time),
    datasets: [
      {
        label: "Global Posture Score",
        data: data?.map((item) => item?.postureScore),
        borderColor: "#f97316",
        backgroundColor: gradientOrange,
        yAxisID: "yLeft", // Left Axis
        pointBackgroundColor: "#fff",
        pointBorderColor: "#f97316",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Total Identities",
        data: data?.map((item) => item?.identityCount),
        borderColor: "#3b82f6",
        backgroundColor: gradientBlue,
        yAxisID: "yRight", // Right Axis
        pointBackgroundColor: "#fff",
        pointBorderColor: "#3b82f6",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const options =useMemo(() => ( {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          color: "#64748b",
          padding: 20,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor:  isDark ? "#1b3157" : "#ffffff",
        titleColor: isDark ? "#E6F1FF" : "#111827",
        bodyColor: isDark ? "#8892B0" : "#374151",
        borderColor:  isDark ? "#233554" : "#E5E7EB",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          // Injects custom logic to show breakdowns on hover
          footer: (tooltipItems) => {
            const dataIndex = tooltipItems[0].dataIndex;
            const targetItem = data[dataIndex];
            if (targetItem) {
              return `  └ Human: ${targetItem.humans}\n  └ Non-Human: ${targetItem.nonHumans}`;
            }
            return "";
          },
        },
        footerColor:  isDark ? "#94a3b8" : "#64748b",
        footerFont: { size: 11, weight: "normal" },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#94a3b8" },
      },
      yLeft: {
        type: "linear",
        position: "left",
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { color: "#f97316" },
        title: {
          display: true,
          text: "Global Posture Score",
          color: "#f97316",
        },
      },
      yRight: {
        type: "linear",
        position: "right",
        grid: { display: false },
        ticks: { color: "#3b82f6" },
        title: {
          display: true,
          text: "Total Identities",
          color: "#3b82f6",
        },
      },
    },
  }), [isDark]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart && !loading) {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      if (chartArea) {
        const orangeGrad = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        orangeGrad.addColorStop(0, "rgba(249, 115, 22, 0.3)");
        orangeGrad.addColorStop(1, "rgba(249, 115, 22, 0.0)");
        setGradientOrange(orangeGrad);

        const blueGrad = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        blueGrad.addColorStop(0, "rgba(59, 130, 246, 0.3)");
        blueGrad.addColorStop(1, "rgba(59, 130, 246, 0.0)");
        setGradientBlue(blueGrad);
      }
    }
  }, [data, loading]);

    if (loading) {
    return (
      <div className={`chart-container skeleton-container chart-light`}>
        <div className="skeleton-header">
          <div className="skeleton-text skeleton-title"></div>
        </div>
        <div className="skeleton-chart-body">
          <div className="skeleton-line-mock"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`chart-container chart-light`}>
      <div className="chart-header-modern">
        <div className="chart-info">
          <h3 className="chart-title">Identity & Posture Timeline</h3>
          <p className="chart-subtitle">Security Posture vs Identity Volume</p>
        </div>
      </div>

      <div className="chart-body-wrapper">
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
};

export default RiskTimeLineChart;